import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { DevBoardItem, DevItemDetail } from './types'

export interface DevBoardParams {
  stream?: string
  state?: string
  sprint?: number
  staleDays?: number
}

function boardSearchParams(params: DevBoardParams): string {
  const usp = new URLSearchParams()
  if (params.stream) usp.set('stream', params.stream)
  if (params.state) usp.set('state', params.state)
  if (params.sprint != null) usp.set('sprint', String(params.sprint))
  if (params.staleDays != null) usp.set('stale_days', String(params.staleDays))
  return usp.toString()
}

export function useDevBoard(params: DevBoardParams = {}) {
  return useQuery({
    queryKey: ['dev-tracker', 'board', params],
    queryFn: () => jsonFetch<DevBoardItem[]>(`/api/dev/board?${boardSearchParams(params)}`),
    staleTime: 30_000,
  })
}

export function useDevItem(ref: string | null) {
  return useQuery({
    queryKey: ['dev-tracker', 'item', ref],
    queryFn: () => jsonFetch<DevItemDetail>(`/api/dev/items/${encodeURIComponent(ref ?? '')}`),
    enabled: !!ref,
    staleTime: 30_000,
  })
}

export function useDevQueue(staleDays?: number) {
  return useQuery({
    queryKey: ['dev-tracker', 'queue', staleDays],
    queryFn: () =>
      jsonFetch<DevBoardItem[]>(
        `/api/dev/queue${staleDays != null ? `?stale_days=${staleDays}` : ''}`,
      ),
    staleTime: 30_000,
  })
}

// ─── Phase 4: transition / accept-proposal / dismiss-proposal ────────

export interface TransitionRequest {
  toState: string
  displacesRef?: string
  hotfixOverride?: boolean
  reason?: string
}

/** Shared invalidation for every mutation below — the item detail AND
 * every board/queue list may now show stale data (this item's row, or
 * (for a displacement) a DIFFERENT item's row). Broad invalidation over
 * precise patching: this is a low-traffic internal tool, not worth the
 * complexity of hand-patching every affected list. */
function useInvalidateDevTracker() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['dev-tracker'] })
}

export function useTransitionDevItem(ref: string | null) {
  const invalidate = useInvalidateDevTracker()
  return useMutation({
    mutationFn: (req: TransitionRequest) =>
      jsonFetch<DevItemDetail>(`/api/dev/items/${encodeURIComponent(ref ?? '')}/transition`, {
        method: 'POST',
        body: {
          to_state: req.toState,
          displaces_ref: req.displacesRef,
          hotfix_override: req.hotfixOverride ?? false,
          reason: req.reason,
        },
      }),
    onSuccess: invalidate,
  })
}

export function useAcceptDevItemProposal(ref: string | null) {
  const invalidate = useInvalidateDevTracker()
  return useMutation({
    mutationFn: () =>
      jsonFetch<DevItemDetail>(
        `/api/dev/items/${encodeURIComponent(ref ?? '')}/accept-proposal`,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  })
}

export function useDismissDevItemProposal(ref: string | null) {
  const invalidate = useInvalidateDevTracker()
  return useMutation({
    mutationFn: () =>
      jsonFetch<DevItemDetail>(
        `/api/dev/items/${encodeURIComponent(ref ?? '')}/dismiss-proposal`,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  })
}
